/**
 * Godown Assignment Page — Mill Lot Wise Storage Allocation
 * ─────────────────────────────────────────────────────────────────────────────
 * Store Worker's screen. Shows all Mill Lots with their godown assignment status.
 *
 * Workflow:
 *   1. Worker selects a Mill Lot from the queue
 *   2. Enters Godown / Row / Col / Level
 *   3. Clicks "Assign All Bales" → all bales in that lot get the same location
 *   4. Status becomes PENDING_APPROVAL → Store Manager approves on Godown Approval page
 *
 * Status legend:
 *   No Godown       → needs assignment (red indicator)
 *   PENDING_APPROVAL → assigned, waiting Store Manager sign-off (orange)
 *   APPROVED         → confirmed (green)
 */
import { useState } from 'react'
import {
  Alert, App, Badge, Button, Card, Col, Descriptions, Drawer, Empty,
  Form, Input, Row, Select, Space, Statistic, Table, Tag, Typography, Popconfirm,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  EnvironmentOutlined, ReloadOutlined, SendOutlined,
  WarningOutlined, CheckCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface LotGodownSummary {
  mill_lot_id: string
  mill_lot_no: string | null
  cotton_variety: string | null
  crop_year: string | null
  supplier_lot_ref: string | null
  total_bales: number
  no_godown: number
  pending_approval: number
  approved: number
  godown: string | null
  row_no: string | null
  col_no: string | null
  level: string | null
  // QC status counts
  qc_pending: number
  qc_approved: number
  qc_hold: number
  qc_rejected: number
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function lotStatus(lot: LotGodownSummary): 'unassigned' | 'pending' | 'approved' | 'partial' {
  if (lot.no_godown > 0 && lot.pending_approval === 0 && lot.approved === 0) return 'unassigned'
  if (lot.pending_approval > 0) return 'pending'
  if (lot.approved === lot.total_bales) return 'approved'
  return 'partial'
}

const STATUS_TAG: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
  unassigned: { color: 'red',    label: 'Not Assigned',    icon: <WarningOutlined /> },
  pending:    { color: 'orange', label: 'Pending Approval', icon: <ClockCircleOutlined /> },
  approved:   { color: 'green',  label: 'All Confirmed',    icon: <CheckCircleOutlined /> },
  partial:    { color: 'gold',   label: 'Partial',          icon: <ClockCircleOutlined /> },
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
export default function GodownAssignmentPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()

  const [form] = Form.useForm()
  const [selectedLot, setSelectedLot] = useState<LotGodownSummary | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Fetch mill-lot godown summary
  const { data: lots = [], isLoading, refetch } = useQuery<LotGodownSummary[]>({
    queryKey: ['mill-lot-godown-summary', plantId],
    queryFn: () =>
      apiClient
        .get('/bales/mill-lot-godown-summary', { params: { plant_id: plantId } })
        .then(r => r.data),
    enabled: !!plantId,
  })

  // Load godown options from Store master
  const { data: godownsData } = useQuery<{ items: any[] }>({
    queryKey: ['stores-godowns', plantId],
    queryFn: () =>
      apiClient.get('/stores', { params: { plant_id: plantId, store_type: 'GODOWN', page_size: 200 } })
        .then(r => r.data),
    enabled: !!plantId,
  })
  const godownOptions = (godownsData?.items ?? []).map((s: any) => ({
    value: s.store_name,
    label: s.store_name,
  }))

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiClient.post('/bales/assign-godown-by-lot', payload).then(r => r.data),
    onSuccess: (data) => {
      message.success(`✓ ${data.updated} bales assigned to ${data.godown} — awaiting Store Manager approval`)
      // Cross-invalidate both lists so the Approval page sees these new
      // Pending bales instantly (no 30-second wait for refetchInterval).
      qc.invalidateQueries({ queryKey: ['mill-lot-godown-summary', plantId] })
      qc.invalidateQueries({ queryKey: ['godown-pending', plantId] })
      setDrawerOpen(false)
      setSelectedLot(null)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Assignment failed'),
  })

  const handleAssign = (lot: LotGodownSummary) => {
    setSelectedLot(lot)
    form.setFieldsValue({
      godown: lot.godown ?? undefined,
      row_no: lot.row_no ?? undefined,
      col_no: lot.col_no ?? undefined,
      level: lot.level ?? undefined,
    })
    setDrawerOpen(true)
  }

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      if (!selectedLot) return
      assignMutation.mutate({
        mill_lot_id: selectedLot.mill_lot_id,
        godown: vals.godown,
        row_no: vals.row_no || null,
        col_no: vals.col_no || null,
        level: vals.level || null,
      })
    })
  }

  // Summary counts
  const unassigned = lots.filter(l => lotStatus(l) === 'unassigned').length
  const pending    = lots.filter(l => lotStatus(l) === 'pending').length
  const approved   = lots.filter(l => lotStatus(l) === 'approved').length

  const columns: ColumnsType<LotGodownSummary> = [
    {
      title: 'Mill Lot No',
      dataIndex: 'mill_lot_no',
      key: 'ml',
      width: 140,
      render: (v: string) => <Text code style={{ fontSize: 12 }}>{v || '—'}</Text>,
    },
    {
      title: 'Variety / Crop',
      key: 'variety',
      width: 160,
      render: (_: unknown, r: LotGodownSummary) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12, fontWeight: 600 }}>{r.cotton_variety || '—'}</Text>
          {r.crop_year && <Text type="secondary" style={{ fontSize: 11 }}>{r.crop_year}</Text>}
          {r.supplier_lot_ref && (
            <Text type="secondary" style={{ fontSize: 10 }}>Lot: {r.supplier_lot_ref}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Bales',
      key: 'bales',
      width: 130,
      render: (_: unknown, r: LotGodownSummary) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          <span><b>{r.total_bales}</b> total</span>
          {r.no_godown > 0 && (
            <Tag color="red" style={{ fontSize: 9, margin: 0 }}>
              {r.no_godown} need location
            </Tag>
          )}
          {r.pending_approval > 0 && (
            <Tag color="orange" style={{ fontSize: 9, margin: 0 }}>
              {r.pending_approval} awaiting mgr
            </Tag>
          )}
          {r.approved > 0 && (
            <Tag color="green" style={{ fontSize: 9, margin: 0 }}>
              {r.approved} confirmed
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Current Location',
      key: 'location',
      width: 150,
      render: (_: unknown, r: LotGodownSummary) => r.godown ? (
        <Space direction="vertical" size={1}>
          <Tag icon={<EnvironmentOutlined />} color="blue" style={{ fontSize: 11 }}>
            {r.godown}
          </Tag>
          {(r.row_no || r.col_no || r.level) && (
            <Text type="secondary" style={{ fontSize: 10 }}>
              {[r.row_no, r.col_no, r.level].filter(Boolean).join(' / ')}
            </Text>
          )}
        </Space>
      ) : <Text type="secondary" style={{ fontSize: 11 }}>Not assigned</Text>,
    },
    {
      title: 'QC Status',
      key: 'qc',
      width: 150,
      render: (_: unknown, r: LotGodownSummary) => {
        const total = (r.qc_pending ?? 0) + (r.qc_approved ?? 0) + (r.qc_hold ?? 0) + (r.qc_rejected ?? 0)
        if (total === 0) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>
        return (
          <Space direction="vertical" size={1}>
            {(r.qc_hold ?? 0) > 0 && (
              <Tag color="red" icon={<WarningOutlined />} style={{ fontSize: 10, margin: 0 }}>
                {r.qc_hold} On Hold
              </Tag>
            )}
            {(r.qc_pending ?? 0) > 0 && (
              <Tag color="orange" icon={<ClockCircleOutlined />} style={{ fontSize: 10, margin: 0 }}>
                {r.qc_pending} Pending
              </Tag>
            )}
            {(r.qc_approved ?? 0) > 0 && (
              <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 10, margin: 0 }}>
                {r.qc_approved} Approved
              </Tag>
            )}
            {(r.qc_rejected ?? 0) > 0 && (
              <Tag color="volcano" style={{ fontSize: 10, margin: 0 }}>
                {r.qc_rejected} Rejected
              </Tag>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Godown Status',
      key: 'status',
      width: 140,
      render: (_: unknown, r: LotGodownSummary) => {
        const s = lotStatus(r)
        const cfg = STATUS_TAG[s]
        return (
          <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 11 }}>
            {cfg.label}
          </Tag>
        )
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_: unknown, r: LotGodownSummary) => {
        const s = lotStatus(r)
        return (
          <Button
            size="small"
            type={s === 'unassigned' ? 'primary' : 'default'}
            icon={<EnvironmentOutlined />}
            onClick={() => handleAssign(r)}
          >
            {s === 'unassigned' ? 'Assign' : 'Re-assign'}
          </Button>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Godown Assignment"
        subtitle="Assign warehouse storage location to bales — Mill Lot wise"
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
        }
      />

      {/* KPI strip */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={8}>
          <Card size="small" style={{ borderTop: '3px solid #ff4d4f' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Not Assigned</span>}
              value={unassigned}
              valueStyle={{ color: '#ff4d4f', fontWeight: 700, fontSize: 24 }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>Pending Approval</span>}
              value={pending}
              valueStyle={{ color: '#faad14', fontWeight: 700, fontSize: 24 }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
            <Statistic
              title={<span style={{ fontSize: 11 }}>All Confirmed</span>}
              value={approved}
              valueStyle={{ color: '#52c41a', fontWeight: 700, fontSize: 24 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Unassigned alert */}
      {unassigned > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${unassigned} Mill Lot(s) have bales with no godown assigned. Assign a location so the Store Manager can verify placement.`}
        />
      )}

      {/* Main table */}
      <Card size="small" title={
        <Space>
          <Badge count={unassigned + pending} color={unassigned > 0 ? '#ff4d4f' : '#faad14'} showZero={false} />
          <span style={{ fontWeight: 600 }}>Mill Lot Storage Allocation</span>
        </Space>
      }>
        {!plantId ? (
          <Empty description="Select a plant first" />
        ) : (
          <Table
            rowKey="mill_lot_id"
            size="small"
            loading={isLoading}
            dataSource={lots}
            columns={columns}
            scroll={{ x: 800 }}
            rowClassName={(r) => {
              const s = lotStatus(r)
              if (s === 'unassigned') return 'ant-table-row-danger'
              if (s === 'pending') return 'ant-table-row-warning'
              return ''
            }}
            pagination={{ pageSize: 50, showSizeChanger: false, showTotal: t => `${t} Mill Lots` }}
            locale={{ emptyText: <Empty description="No Mill Lots found. Create GRNs first." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
        )}
      </Card>

      {/* Assignment Drawer */}
      <Drawer
        title={
          <Space>
            <EnvironmentOutlined style={{ color: '#1677ff' }} />
            <span>Assign Godown — {selectedLot?.mill_lot_no}</span>
          </Space>
        }
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedLot(null); form.resetFields() }}
        width={420}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setDrawerOpen(false); form.resetFields() }}>Cancel</Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={assignMutation.isPending}
              onClick={handleSubmit}
            >
              Assign All {selectedLot?.total_bales} Bales
            </Button>
          </Space>
        }
      >
        {selectedLot && (
          <>
            {/* Lot summary */}
            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Mill Lot No">
                <Text code>{selectedLot.mill_lot_no}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Variety">
                <b>{selectedLot.cotton_variety || '—'}</b>
                {selectedLot.crop_year && <Text type="secondary"> ({selectedLot.crop_year})</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier Lot">{selectedLot.supplier_lot_ref || '—'}</Descriptions.Item>
              <Descriptions.Item label="Total Bales">
                <Space>
                  <Tag color="blue">{selectedLot.total_bales} bales</Tag>
                  {selectedLot.no_godown > 0 && <Tag color="red">{selectedLot.no_godown} unassigned</Tag>}
                  {selectedLot.pending_approval > 0 && <Tag color="orange">{selectedLot.pending_approval} pending</Tag>}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="All bales in this Mill Lot will be assigned the same location."
              description="After assignment, the Store Manager must physically verify and approve each location."
            />

            <Form form={form} layout="vertical">
              <Form.Item
                name="godown"
                label="Godown / Warehouse"
                rules={[{ required: true, message: 'Select a godown' }]}
              >
                <Select
                  showSearch
                  placeholder="Select godown from master…"
                  options={godownOptions}
                  notFoundContent={
                    godownOptions.length === 0
                      ? <span style={{ fontSize: 12, color: '#aaa' }}>No godowns — create in Masters → Stores</span>
                      : 'No match'
                  }
                />
              </Form.Item>

              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="row_no" label="Row">
                    <Input placeholder="e.g. R-3" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="col_no" label="Column">
                    <Input placeholder="e.g. C-5" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="level" label="Level">
                    <Input placeholder="e.g. L-1" />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{
                padding: '10px 14px',
                background: '#fff7e6',
                borderRadius: 6,
                border: '1px solid #ffd591',
                marginTop: 4,
              }}>
                <Text style={{ fontSize: 12, color: '#874d00' }}>
                  ⚠️ This will overwrite the current location for all <b>{selectedLot.total_bales}</b> bales
                  in Mill Lot <b>{selectedLot.mill_lot_no}</b> and reset approval to <b>Pending</b>.
                </Text>
              </div>
            </Form>
          </>
        )}
      </Drawer>
    </div>
  )
}
